/**
 * 全局 Toast 工具 — 绕过 useToastController hook 限制
 * 允许在组件外 (API client、事件处理器) 调用 toast.success() / toast.error()
 */
import {
  useToastController,
  Toaster,
  ToastTitle,
  ToastBody,
  Toast,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { createElement } from 'react'

// ── Toast 类型 ──
type ToastVariant = 'success' | 'error' | 'warning' | 'info'

// ── 样式 ──
const useStyles = makeStyles({
  toastSuccess: { borderLeftColor: tokens.colorStatusSuccessForeground1 },
  toastError: { borderLeftColor: tokens.colorStatusDangerForeground1 },
  toastWarning: { borderLeftColor: tokens.colorStatusWarningForeground1 },
  toastInfo: { borderLeftColor: tokens.colorBrandForeground1 },
})

function variantToClass(v: ToastVariant, styles: ReturnType<typeof useStyles>) {
  switch (v) {
    case 'success': return styles.toastSuccess
    case 'error': return styles.toastError
    case 'warning': return styles.toastWarning
    case 'info': return styles.toastInfo
  }
}

// ── 全局 ToastBridge ──
let _dispatch: ((variant: ToastVariant, title: string, body?: string) => void) | null = null

/** 在应用根节点渲染 <ToastBridge /> 以激活全局 toast */
export function ToastBridge() {
  const { dispatchToast } = useToastController('mailman-toaster')
  const styles = useStyles()

  _dispatch = (variant: ToastVariant, title: string, body?: string) => {
    dispatchToast(
      createElement(
        Toast,
        { key: Date.now().toString(), className: variantToClass(variant, styles) },
        createElement(ToastTitle, null, title),
        body ? createElement(ToastBody, null, body) : null,
      ),
      // timeout 语义：0=立即消失、-1=驻留直至手动关闭、其余为毫秒
      { timeout: variant === 'error' ? -1 : 3000 }
    )
  }

  return null
}

// ── 对外 API ──
export const toast = {
  success(title: string, body?: string) {
    _dispatch?.('success', title, body)
  },
  error(title: string, body?: string) {
    _dispatch?.('error', title, body)
  },
  warning(title: string, body?: string) {
    _dispatch?.('warning', title, body)
  },
  info(title: string, body?: string) {
    _dispatch?.('info', title, body)
  },
}

// ── Toaster 容器 (在 main.tsx 中渲染) ──
export function GlobalToaster() {
  return createElement(Toaster, { toasterId: 'mailman-toaster', position: 'top-end' })
}
